import React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { useCreateCaseThread } from '../../../data/support/useSupport';
import { CaseThreadOrigin } from '../../../types/support.types';

interface CreateThreadDialogProps {
  open: boolean;
  onClose: () => void;
  caseId: string;
  onCreated?: (threadId: string) => void;
}

const ORIGIN_OPTIONS: CaseThreadOrigin[] = ['app', 'email', 'slack'];

const CreateThreadDialog: React.FC<CreateThreadDialogProps> = ({
  open,
  onClose,
  caseId,
  onCreated,
}) => {
  const [subject, setSubject] = React.useState('');
  const [origin, setOrigin] = React.useState<CaseThreadOrigin>('app');
  const [error, setError] = React.useState<string | null>(null);
  const createThread = useCreateCaseThread(caseId);

  const handleSubmit = () => {
    setError(null);
    createThread.mutate(
      { label: subject, origin },
      {
        onSuccess: (thread) => {
          resetForm();
          onClose();
          onCreated?.(thread.id);
        },
        onError: (err: any) => {
          const detail =
            err?.response?.data?.detail || 'Failed to create thread';
          setError(detail);
        },
      },
    );
  };

  const resetForm = () => {
    setSubject('');
    setOrigin('app');
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Thread</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          autoFocus
          label="Subject"
          fullWidth
          size="small"
          required
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          sx={{ mt: 1, mb: 2 }}
        />
        <TextField
          label="Origin"
          fullWidth
          size="small"
          select
          value={origin}
          onChange={(e) => setOrigin(e.target.value as CaseThreadOrigin)}
        >
          {ORIGIN_OPTIONS.map((o) => (
            <MenuItem key={o} value={o}>
              {o.charAt(0).toUpperCase() + o.slice(1)}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!subject || createThread.isPending}
        >
          {createThread.isPending ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateThreadDialog;
